import { Box, CircularProgress, Typography } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ComplianceTab from '../components/ComplianceTab';
import { catIconSrc, catOf, JUDGE_LABEL, SEAL } from '../components/complianceAssets';
import MikiwameLoader from '../components/MikiwameLoader';
import { useThemeColors } from '../hooks/useThemeColors';
import { getComplianceResult, getItemThumbnail, startComplianceCheck } from '../services/api';
import { getPersonalItems } from '../services/factCheckItemService';
import { getValidPdfUrl, getValidVideoUrl } from '../services/gcs';
import { IMG } from '../theme/assets';
import type { ComplianceStatus, FactCheckItem } from '../types';

// 規程チェック可能な対象（抽出/解析が済んでソースが揃っているもの）
function isEligible(it: FactCheckItem): boolean {
  if (it.type === 'video_upload' || it.type === 'video_url') {
    return it.analysisStatus === 'completed';
  }
  if (it.type === 'document') {
    return ['extracted', 'verifying', 'completed'].includes(it.status);
  }
  return false;
}

const isVideoItem = (it: FactCheckItem) => it.type !== 'document';

/** 一覧の副見出し。動画は長さ、文書はページ数（無ければ空） */
const metaOf = (it: FactCheckItem): string => {
  if (isVideoItem(it) && it.durationSec) {
    const s = Math.floor(it.durationSec);
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  }
  return '';
};

const fmtWhen = (iso?: string): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const isChecked = (it: FactCheckItem) => it.complianceStatus === 'completed';
const isChecking = (it: FactCheckItem) =>
  it.complianceStatus === 'pending' || it.complianceStatus === 'checking';
type CState = 'done' | 'checking' | 'todo';
const complianceState = (it: FactCheckItem): CState =>
  isChecked(it) ? 'done' : isChecking(it) ? 'checking' : 'todo';

type StatusFilter = 'all' | 'done' | 'todo';

export default function ComplianceCheck() {
  const t = useThemeColors();
  const [items, setItems] = useState<FactCheckItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null); // 詳細を開いているもの

  // /compliance?item=xxx で開いたときは、そのアイテムの詳細を出す
  // biome-ignore lint/correctness/useExhaustiveDependencies: 初回の URL だけ見たい
  useEffect(() => {
    const fromUrl = searchParams.get('item');
    if (fromUrl) setSelectedId(fromUrl);
  }, []);
  const [previewId, setPreviewId] = useState<string | null>(null); // 右ペインに出しているもの
  const [query, setQuery] = useState('');
  const [previewCats, setPreviewCats] = useState<[string, number][]>([]);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [startingIds, setStartingIds] = useState<Set<string>>(new Set());
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [pdfUrl, setPdfUrl] = useState<string>('');

  // 一覧を（再）取得。チェック実行/完了後にステータスを反映するため、戻る時やポーリングでも呼ぶ
  const refreshItems = useCallback(async () => {
    try {
      const all = await getPersonalItems();
      setItems(all.filter(isEligible));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshItems();
  }, [refreshItems]);

  // 一覧のカードから直接チェックを開始する。詳細画面へ入らずに実行できるようにする
  // （以前はカードのボタンが詳細を開くだけで、「実行」の文言と動作が合っていなかった）
  const runCheck = useCallback(async (id: string) => {
    setStartingIds((prev) => new Set(prev).add(id));
    const res = await startComplianceCheck(id);
    setStartingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    if (res.success) {
      // 楽観更新でカードを「チェック中」にする。以降は下のポーリングが完了を反映する
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, complianceStatus: 'pending' } : it)),
      );
    }
  }, []);

  // 一覧表示中にチェック中のアイテムがあれば定期更新（完了を自動反映）
  useEffect(() => {
    if (selectedId) return;
    if (!items.some(isChecking)) return;
    const id = setTimeout(refreshItems, 6000);
    return () => clearTimeout(id);
  }, [items, selectedId, refreshItems]);

  // サムネイル（動画=代表フレーム / PDF=1ページ目）は、右ペインに出すものだけ取りに行く。
  // 以前は一覧の全件を取りに行っており、しかも thumbs を依存に持っていたため
  // 取得→再実行→残りを再取得…と連鎖して15件で120リクエスト出ていた（画像欠けの原因）。
  const requestedThumbs = useRef<Set<string>>(new Set());
  const [thumbTarget, setThumbTarget] = useState<string | null>(null);
  useEffect(() => {
    if (!thumbTarget || requestedThumbs.current.has(thumbTarget)) return;
    requestedThumbs.current.add(thumbTarget);
    let alive = true;
    getItemThumbnail(thumbTarget).then((url) => {
      if (alive && url) setThumbs((m) => ({ ...m, [thumbTarget]: url }));
    });
    return () => {
      alive = false;
    };
  }, [thumbTarget]);

  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  );

  // 右ペインの観点内訳。件数は一覧データで足りるが、観点は findings を見ないと分からない
  useEffect(() => {
    setPreviewCats([]);
    const it = items.find((i) => i.id === previewId);
    if (!it || !isChecked(it)) return;
    let alive = true;
    getComplianceResult(it.id).then((res) => {
      if (!alive || !res.success || !res.data?.findings) return;
      const m = new Map<string, number>();
      for (const f of res.data.findings) {
        const k = f.category || 'その他・一般';
        m.set(k, (m.get(k) ?? 0) + 1);
      }
      setPreviewCats([...m.entries()].sort((a, b) => b[1] - a[1]));
    });
    return () => {
      alive = false;
    };
  }, [previewId, items]);

  // 選択アイテムのプレビューURL取得（動画=署名URL / 文書=PDF URL）
  useEffect(() => {
    setVideoUrl('');
    setPdfUrl('');
    if (!selected) return;
    (async () => {
      try {
        if (isVideoItem(selected)) {
          setVideoUrl(
            await getValidVideoUrl(selected.id, selected.fileUrl || '', selected.blobName || ''),
          );
        } else {
          setPdfUrl(await getValidPdfUrl(selected.id, selected.fileUrl || ''));
        }
      } catch {
        /* プレビュー無しでも継続 */
      }
    })();
  }, [selected]);

  // ===== 詳細画面（固定シェル＋左findingsスクロール。戻る/hero/フィルタはComplianceTab内） =====
  if (selected) {
    // 100vh は body の zoom(--ui-scale) でずれて下に余白が出るため --app-vh を使う
    return (
      <Box sx={{ height: 'calc(var(--app-vh) - 64px - 64px)' }}>
        <ComplianceTab
          key={selected.id}
          itemId={selected.id}
          title={selected.title || selected.id}
          isVideo={isVideoItem(selected)}
          videoUrl={videoUrl}
          pdfUrl={pdfUrl}
          onBack={() => {
            setSelectedId(null);
            refreshItems();
          }}
          onStatusChange={(s) =>
            setItems((prev) =>
              prev.map((it) =>
                it.id === selected.id ? { ...it, complianceStatus: s as ComplianceStatus } : it,
              ),
            )
          }
        />
      </Box>
    );
  }

  // ===== 入口（一覧）＝ 左に一覧・右に選択中のプレビュー =====
  const total = items.length;
  const doneCount = items.filter(isChecked).length;
  const checkingCount = items.filter(isChecking).length;
  const todoCount = total - doneCount - checkingCount;

  const matchFilter = (it: FactCheckItem) => {
    if (filter === 'done') return isChecked(it);
    if (filter === 'todo') return !isChecked(it) && !isChecking(it);
    return true;
  };
  const matchQuery = (it: FactCheckItem) =>
    !query.trim() || (it.title || '').toLowerCase().includes(query.trim().toLowerCase());

  // 済んだものを上に（新しい順）、次にチェック中、最後に未チェック。
  // 「結果を見に来た」場合に目的のものが上に来る。
  const ORDER: Record<CState, number> = { done: 0, checking: 1, todo: 2 };
  const rows = items
    .filter((it) => matchFilter(it) && matchQuery(it))
    .sort((a, b) => {
      const d = ORDER[complianceState(a)] - ORDER[complianceState(b)];
      if (d !== 0) return d;
      return (b.complianceCheckedAt ?? '').localeCompare(a.complianceCheckedAt ?? '');
    });

  const preview = rows.find((it) => it.id === previewId) ?? rows[0] ?? null;

  if (preview && thumbTarget !== preview.id) setThumbTarget(preview.id);

  const chip = (key: StatusFilter, label: string, n: number) => {
    const on = filter === key;
    return (
      <Box
        key={key}
        component="button"
        onClick={() => setFilter(key)}
        sx={{
          fontFamily: 'inherit',
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 999,
          px: 1.5,
          py: 0.6,
          cursor: 'pointer',
          border: `1px solid ${on ? 'transparent' : t.line}`,
          backgroundColor: on ? t.teal : 'transparent',
          color: on ? '#fff' : t.muted,
        }}
      >
        {label} <b style={{ fontWeight: 900 }}>{n}</b>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        // Layout 側で既に p:4 が入っているので、ここでは左右に足さない
        height: 'calc(var(--app-vh) - 64px - 64px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {/* 見出し（1行） */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          borderRadius: 2,
          px: 2,
          py: 1.25,
          border: `1px solid ${t.line}`,
          background: `linear-gradient(120deg, ${t.heroFrom}, ${t.heroTo})`,
        }}
      >
        <Box component="img" src={IMG.owl.clipboard} alt="" sx={{ width: 42, height: 42 }} />
        <Box>
          <Typography sx={{ fontSize: 19, fontWeight: 900, color: t.ink }}>
            放送<span style={{ color: t.teal }}>規程</span>チェック
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: t.muted }}>
            基準・ハンドブックに照らして点検・根拠条文を提示
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, color: t.muted }}>
          対象 <b style={{ color: t.ink }}>{total}</b> ／ チェック済み{' '}
          <b style={{ color: t.ink }}>{doneCount}</b>
        </Typography>
      </Box>

      {/* 2ペイン */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          // 行を内容なりに伸ばさない。伸びるとパネルからはみ出し、
          // 一覧が自前でスクロールできないまま最下行が切れる
          gridTemplateRows: 'minmax(0, 1fr)',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 420px', xl: '1fr 470px' },
          backgroundColor: t.paper,
          border: `1px solid ${t.line}`,
          borderRadius: 2,
          boxShadow: '0 6px 20px rgba(30,30,70,.06)',
          overflow: 'hidden',
        }}
      >
        {/* 左：一覧 */}
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: { lg: `1px solid ${t.line}` },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              px: 2,
              py: 1.25,
              borderBottom: `1px solid ${t.line}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.9,
                border: `1px solid ${t.line}`,
                borderRadius: 1.25,
                px: 1.25,
                flex: 1,
                minWidth: 160,
                maxWidth: 280,
                '&:focus-within': { borderColor: t.teal },
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{ width: 15, height: 15, fill: t.faint, flexShrink: 0 }}
              >
                <path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14" />
              </Box>
              <Box
                component="input"
                placeholder="タイトルで探す"
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                sx={{
                  fontFamily: 'inherit',
                  fontSize: 12,
                  color: t.ink,
                  backgroundColor: 'transparent',
                  border: 'none',
                  py: 0.85,
                  flex: 1,
                  minWidth: 0,
                  '&::placeholder': { color: t.faint },
                  '&:focus': { outline: 'none' },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {chip('all', 'すべて', total)}
              {chip('todo', '未チェック', todoCount)}
              {chip('done', 'チェック済み', doneCount)}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              // 下端が切れている＝まだ続きがある、が分かるように薄く陰を落とす
              maskImage: 'linear-gradient(to bottom, #000 calc(100% - 18px), transparent)',
            }}
          >
            {loading ? (
              <MikiwameLoader label="対象を読み込んでいます" size={128} />
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: t.faint, fontSize: 13 }}>
                {total === 0
                  ? '対象データがありません（抽出/映像解析が完了したアイテムが対象です）。'
                  : '該当するアイテムはありません。'}
              </Box>
            ) : (
              <ItemTable rows={rows} previewId={preview?.id ?? null} onPick={setPreviewId} t={t} />
            )}
          </Box>
        </Box>

        {/* 右：選択中のプレビュー */}
        <Box sx={{ display: { xs: 'none', lg: 'block' }, minHeight: 0, overflowY: 'auto', p: 2 }}>
          {preview ? (
            <PreviewPane
              it={preview}
              thumb={thumbs[preview.id]}
              cats={previewCats}
              starting={startingIds.has(preview.id)}
              onOpen={() => setSelectedId(preview.id)}
              onRun={() => runCheck(preview.id)}
              t={t}
            />
          ) : (
            <Typography sx={{ fontSize: 12.5, color: t.faint }}>
              左から選ぶと、ここに中身と結果が出ます。
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ===== 一覧（種別・タイトル・状態・結果・チェック日時） =====
function ItemTable({
  rows,
  previewId,
  onPick,
  t,
}: {
  rows: FactCheckItem[];
  previewId: string | null;
  onPick: (id: string) => void;
  t: ReturnType<typeof useThemeColors>;
}) {
  const th = {
    fontSize: 10,
    fontWeight: 800,
    color: t.faint,
    letterSpacing: '.06em',
    textAlign: 'left' as const,
    padding: '9px 12px',
    position: 'sticky' as const,
    top: 0,
    backgroundColor: t.paper,
    borderBottom: `1px solid ${t.line}`,
    zIndex: 1,
  };
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" sx={{ ...th, width: 78 }}>
            種別
          </Box>
          <Box component="th" sx={th}>
            タイトル
          </Box>
          <Box component="th" sx={{ ...th, width: 104 }}>
            状態
          </Box>
          <Box component="th" sx={{ ...th, width: 134 }}>
            結果
          </Box>
          <Box component="th" sx={{ ...th, width: 98 }}>
            チェック日時
          </Box>
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((it) => {
          const on = it.id === previewId;
          const td = {
            padding: '9px 12px',
            // 罫線は薄く。行が多いと線のほうが目立ってしまう
            borderBottom: `1px solid ${t.line}`,
            opacity: 1,
            verticalAlign: 'middle' as const,
          };
          return (
            <Box
              component="tr"
              key={it.id}
              onClick={() => onPick(it.id)}
              sx={{
                cursor: 'pointer',
                backgroundColor: on ? `${t.teal}14` : 'transparent',
                boxShadow: on ? `inset 3px 0 0 ${t.teal}` : 'none',
                '&:hover': { backgroundColor: on ? `${t.teal}14` : t.heroFrom },
              }}
            >
              <Box component="td" sx={{ ...td, whiteSpace: 'nowrap' }}>
                <KindCell it={it} t={t} />
              </Box>
              <Box
                component="td"
                sx={{ ...td, fontSize: 13, fontWeight: 700, color: on ? t.teal : t.ink }}
              >
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.title || it.id}
                </Box>
                {metaOf(it) && (
                  <Typography sx={{ fontSize: 10.5, color: t.muted, fontWeight: 400, mt: 0.2 }}>
                    {metaOf(it)}
                  </Typography>
                )}
              </Box>
              <Box component="td" sx={td}>
                <StatePill state={complianceState(it)} t={t} />
              </Box>
              <Box component="td" sx={td}>
                <ResultCell it={it} t={t} />
              </Box>
              <Box
                component="td"
                sx={{ ...td, fontSize: 10.5, color: t.muted, whiteSpace: 'nowrap' }}
              >
                {fmtWhen(it.complianceCheckedAt) ?? ''}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const VIDEO_PATH =
  'M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z';
const DOC_PATH =
  'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z';

function KindCell({ it, t }: { it: FactCheckItem; t: ReturnType<typeof useThemeColors> }) {
  const v = isVideoItem(it);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.7 }}>
      <Box
        component="svg"
        viewBox="0 0 24 24"
        sx={{ width: 16, height: 16, fill: t.muted, flexShrink: 0 }}
      >
        <path d={v ? VIDEO_PATH : DOC_PATH} />
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.muted }}>
        {v ? '動画' : '文書'}
      </Typography>
    </Box>
  );
}

function StatePill({ state, t }: { state: CState; t: ReturnType<typeof useThemeColors> }) {
  const m = {
    done: { label: 'チェック済み', color: t.teal },
    checking: { label: 'チェック中', color: t.verdict.partial.color },
    todo: { label: '未チェック', color: t.indigo },
  }[state];
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: m.color }} />
      <Typography
        sx={{ fontSize: 11, fontWeight: 700, color: state === 'todo' ? t.muted : m.color }}
      >
        {m.label}
      </Typography>
    </Box>
  );
}

/** 結果の内訳。古いデータは内訳を持たないので総数だけ出す */
function ResultCell({ it, t }: { it: FactCheckItem; t: ReturnType<typeof useThemeColors> }) {
  const state = complianceState(it);
  if (state === 'checking') {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.9 }}>
        <CircularProgress size={11} sx={{ color: t.verdict.partial.color }} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.verdict.partial.color }}>
          チェック中
        </Typography>
      </Box>
    );
  }
  if (state === 'todo') return null;
  const has = it.complianceBadCount != null;
  if (!has) {
    return (
      <Typography sx={{ fontSize: 11.5, color: t.muted }}>
        指摘 <b style={{ color: t.ink }}>{it.complianceFindingCount ?? 0}</b>
      </Typography>
    );
  }
  const color = {
    bad: t.verdict.inaccurate.color,
    caution: t.verdict.partial.color,
    unjudged: t.verdict.unverifiable.color,
  };
  // 0 は出さない。3つとも並ぶと表がうるさく、目が「0」を読まされるため
  const counts = (
    [
      ['bad', it.complianceBadCount ?? 0],
      ['caution', it.complianceCautionCount ?? 0],
      ['unjudged', it.complianceUnjudgedCount ?? 0],
    ] as [keyof typeof SEAL, number][]
  ).filter(([, n]) => n > 0);
  if (counts.length === 0) {
    return (
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: t.teal }}>指摘なし</Typography>
    );
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
      {counts.map(([k, n]) => (
        <Box
          key={k}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.45 }}
          title={JUDGE_LABEL[k]}
        >
          <Box component="img" src={SEAL[k]} alt="" sx={{ width: 18, height: 18 }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: color[k] }}>{n}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ===== 右ペイン =====
function PreviewPane({
  it,
  thumb,
  cats,
  starting,
  onOpen,
  onRun,
  t,
}: {
  it: FactCheckItem;
  thumb?: string;
  cats: [string, number][];
  starting: boolean;
  onOpen: () => void;
  onRun: () => void;
  t: ReturnType<typeof useThemeColors>;
}) {
  const v = isVideoItem(it);
  const state = complianceState(it);
  const btn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.6,
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 800,
    borderRadius: 1.25,
    px: 1.75,
    py: 0.9,
    cursor: 'pointer',
    border: 'none',
  } as const;
  return (
    <Box>
      <Box
        sx={{
          borderRadius: 1.5,
          overflow: 'hidden',
          border: `1px solid ${t.line}`,
          backgroundColor: v ? '#000' : '#fff',
          aspectRatio: v ? '16 / 9' : '4 / 3',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {thumb ? (
          <Box
            component="img"
            src={thumb}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: v ? 'cover' : 'contain',
              objectPosition: v ? 'center' : 'top',
              display: 'block',
            }}
          />
        ) : (
          <Box
            component="img"
            src={v ? IMG.item.video : IMG.item.document}
            alt=""
            sx={{ width: 46, height: 46, opacity: 0.85 }}
          />
        )}
      </Box>

      <Typography sx={{ fontSize: 16, fontWeight: 800, color: t.ink, mt: 2, lineHeight: 1.5 }}>
        {it.title || it.id}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
        <KindCell it={it} t={t} />
        {metaOf(it) && (
          <>
            <Box sx={{ width: '1px', height: 11, backgroundColor: t.line }} />
            <Typography sx={{ fontSize: 11, color: t.muted }}>{metaOf(it)}</Typography>
          </>
        )}
        {it.complianceCheckedAt && (
          <>
            <Box sx={{ width: '1px', height: 11, backgroundColor: t.line }} />
            <Typography sx={{ fontSize: 11, color: t.muted }}>
              {fmtWhen(it.complianceCheckedAt)} にチェック
            </Typography>
          </>
        )}
      </Box>

      {state === 'done' ? (
        <>
          <Box sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${t.line}` }}>
            <ResultCell it={it} t={t} />
          </Box>
          {cats.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: t.faint,
                  letterSpacing: '.06em',
                  mt: 2,
                }}
              >
                どんな観点の指摘か
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                {cats.map(([cat, n]) => (
                  <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.5 }}>
                    <Box
                      component="img"
                      src={catIconSrc(cat)}
                      alt=""
                      sx={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }}
                    />
                    <Typography
                      sx={{ fontSize: 12, color: t.ink, width: 108, flexShrink: 0 }}
                      noWrap
                      title={cat}
                    >
                      {catOf(cat).short}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: 5,
                        borderRadius: 1,
                        backgroundColor: t.line,
                        minWidth: 30,
                      }}
                    >
                      <Box
                        sx={{
                          width: `${(n / Math.max(...cats.map(([, c]) => c))) * 100}%`,
                          height: '100%',
                          borderRadius: 1,
                          backgroundColor: t.teal,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: t.muted,
                        width: 18,
                        textAlign: 'right',
                      }}
                    >
                      {n}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
          <Box
            component="button"
            onClick={onOpen}
            sx={{
              ...btn,
              mt: 2,
              color: t.teal,
              backgroundColor: 'transparent',
              border: `1px solid ${t.teal}`,
            }}
          >
            指摘を見る ›
          </Box>
        </>
      ) : state === 'checking' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={14} sx={{ color: t.verdict.partial.color }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.verdict.partial.color }}>
            チェックしています…
          </Typography>
        </Box>
      ) : (
        <>
          <Typography sx={{ fontSize: 11.5, color: t.muted, mt: 1.75, lineHeight: 1.9 }}>
            まだチェックしていません。
            <br />
            必要な資料だけ実行してください。
          </Typography>
          <Box
            component="button"
            disabled={starting}
            onClick={onRun}
            sx={{
              ...btn,
              mt: 1.75,
              color: '#fff',
              backgroundColor: t.indigo,
              opacity: starting ? 0.7 : 1,
              cursor: starting ? 'default' : 'pointer',
            }}
          >
            {starting && <CircularProgress size={12} sx={{ color: '#fff' }} />}
            {starting ? '開始しています…' : 'この資料をチェックする'}
          </Box>
        </>
      )}
    </Box>
  );
}
